<template>
    <transition name="fade">
        <div v-if="isOpen" class="vipps-modal">
            <div class="modal-content relative" v-on-clickaway="closeModal">
                <InnerLoader v-if="isLoading" class="payment-loading" :z-index="999" />
                <div class="modal-header" style="border-bottom: none">
                    <div class="text-right">
                        <i
                            @click="closeModal"
                            class="fas text-grey fa-times text-xl shadow-text-none cursor-pointer"
                        ></i>
                    </div>
                </div>
                <div class="modal-body text-center">
                    <img src="../assets/vipps-logo.png" class="vipps-logo" />
                    <div>
                        <h3 v-if="title" class="vipps-title mt-0 shadow-text-none">{{ title }}</h3>
                        <div class="flex justify-center items-end mt-6">
                            <h4 v-if="!isSubscription" class="text-xl shadow-text-none m-0 mr-1">{{ amount }} kr.</h4>
                            <p v-if="isSubscription" class="text-xl shadow-text-none m-0 mr-1">
                                {{
                                    $t('vipps_modal_amount', {
                                        amount: amount,
                                        frequency: toLower(vippsData.item.interval.unit)
                                    })
                                }}
                            </p>
                            <span v-if="unit" class="text-lg shadow-text-none">/ {{ unitText }}</span>
                        </div>
                        <p v-if="isSubscription" class="text-xl shadow-text-none m-0 mr-1 font-size-14">
                            {{ $t('vipps_modal_subscription_label') }}
                        </p>
                        <div v-if="isSubscription" class="mt-2 inline-flex subscription-box">
                            <input
                                class="mt-1 input font-size-16 text-center subscription-input"
                                value="+47"
                                disabled
                            />
                            <input
                                v-model="phoneNumber"
                                :placeholder="$t('vipps_modal_input_phone')"
                                :class="{
                                    'mt-1 input input-block font-size-16': true,
                                    'is-invalid': errorPhoneNumber
                                }"
                                @input="onInput"
                                name="phone"
                                type="text"
                                maxlength="8"
                                style="padding: 10px"
                            />
                            <div class="invalid-feedback text-left">
                                <span v-if="errorPhoneNumber" class="has-error text-red shadow-text-none font-size-14">
                                    <i class="fas fa-exclamation-circle"></i>
                                    {{ errorPhoneNumberMessage }}
                                </span>
                            </div>
                        </div>
                        <button
                            @click="submit"
                            :disabled="isSubscription && phoneNumber.length === 0"
                            class="btn w-full vipps-open-button"
                        >
                            <h3 v-if="!isSubscription" class="m-0">
                                {{ translate('vipps_modal_button_pay', { amount: amount }) }}
                            </h3>
                            <h3 v-else class="m-0">
                                {{ translate('vipps_modal_button_start_subscribe') }}
                            </h3>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import Request from '@/lib/request.js'
import appConfig from '@/app.config.js'
import { currentUser } from '@/lib/user.js'
import { directive as onClickaway } from 'vue-clickaway'
import InnerLoader from '@/components/InnerLoader'
import helper from '@/lib/helper.js'
import popup from '@/lib/popup'
import helperMixins from '@/mixins/helper.js'

let vippsPopup = null

export default {
    props: {
        open: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ''
        },
        unit: {
            type: String,
            default: ''
        },
        unitText: {
            type: String,
            default: ''
        },
        amount: {
            default: 0
        },
        vippsData: {
            type: Object,
            default: () => {}
        },
        isSubscription: {
            type: Boolean,
            default: false
        }
    },

    mixins: [helperMixins],

    components: {
        InnerLoader
    },

    directives: {
        onClickaway: onClickaway
    },

    watch: {
        open(value) {
            this.isOpen = value
        }
    },

    data() {
        return {
            lastAction: '',
            status: null,
            isOpen: false,
            isLoading: false,
            phoneNumber: '',
            isVippsAppRedirect: false,
            errorPhoneNumber: false,
            errorPhoneNumberMessage: this.$t('purchase_subscription_required_phone_number')
        }
    },

    mounted() {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState == 'hidden') {
                this.isVippsAppRedirect = true
            }

            if (document.visibilityState == 'visible') {
                this.isVippsAppRedirect = false
            }
        })
    },

    methods: {
        toLower(msg) {
            return msg.toLowerCase()
        },

        submitSubscription() {
            let request = new Request()

            var payload = {
                amount: this.amount,
                title: `$${this.amount} kr. per ${this.vippsData.item.interval.unit.toLowerCase()}${
                    this.unit == '24 hours' ? ' access' : ''
                }`,
                is_app: this.isApp(),
                interval: this.vippsData.item.interval,
                phone: this.phoneNumber,
                plan_id: this.vippsData.item.plan_id,
                user_id: currentUser().id
            }

            request
                .post(appConfig.api.vippsCreatePaymentSubscription, payload)
                .then(res => {
                    this.isLoading = false
                    window.location.href = res.data.data.vippsConfirmationUrl
                })
                .catch(() => {
                    this.isLoading = false
                    popup.alert(this.$t('vipps_payment_subscription_fail'), () => {}, {
                        icon: 'error'
                    })
                })
        },

        onInput(e) {
            var value = e.target.value.replace(/[^0-9.]/g, '')

            this.phoneNumber = value
        },

        onNext() {
            if (!this.phoneNumber || this.phoneNumber.length < 8) {
                this.errorPhoneNumber = true

                return
            }

            this.$emit('subscription', false)
        },

        closeModal() {
            this.isLoading = false
            vippsPopup = null
            this.status = null
            this.$emit('close')
        },

        isApp() {
            return !helper.desktop()
        },

        isSafariFamily() {
            return /Safari/.test(navigator.userAgent)
        },

        submit() {
            this.isLoading = true
            let request = new Request()

            var payload = {
                amount: this.amount,
                title: `Pay ${this.amount} kr. / ${this.unit}${this.unit == '24 hours' ? ' access' : ''}`,
                is_app: this.isApp(),
                user_id: currentUser().id
            }

            if (typeof this.vippsData.item.interval != 'undefined') {
                return this.submitSubscription()
            }

            request.post(appConfig.api.vippsCreatePayment, payload).then(res => {
                if (this.isApp()) {
                    var waitSecond = 5

                    setTimeout(() => {
                        this.closeModal()
                        this.isLoading = false

                        if (this.isVippsAppRedirect) {
                            return
                        }
                        // Open modal for warning message,

                        popup.confirm(
                            `If you see this, it means we can not open Vipps application in your device in ${waitSecond} sec. Click "Go to store" if you not install it yet.`,
                            () => {
                                if (helper.iOS() || this.isSafariFamily()) {
                                    window.location.href = 'http://itunes.apple.com/no/app/vipps/id984380185?mt=8'
                                } else {
                                    window.location.href =
                                        'https://play.google.com/store/apps/details?id=no.dnb.vipps&hl=en'
                                }
                            },
                            () => {},
                            {
                                customButtons: {
                                    ok: {
                                        text: 'Go to store',
                                        className:
                                            'btn font-opensans text-md system-button-color system-button-text-color'
                                    }
                                }
                            }
                        )
                    }, waitSecond * 1000)

                    window.location.href = res.data.data.url

                    return
                }

                window.localStorage.setItem(`${currentUser().id}-vipps-orderId`, res.data.data.orderId)

                let screenWidth = window.screen.width
                let screenHeight = window.screen.height

                let left = screenWidth / 2 - 650 / 2
                let top = screenHeight / 2 - 700 / 2

                vippsPopup = window.open(
                    `${new URL(location.href).origin}/vipps-openner?vippsUrl=${btoa(res.data.data.url)}`,
                    'vippsTab',
                    `scrollbars=no, resizable=no, status=no, location=no, toolbar=no,menubar=no, width=650, height=700, left=${left}, top=${top}`
                )
                this.listenMessage(vippsPopup)
                this.isLoading = false
                this.$emit('close', true)
            })
        },

        checkIsPopupClose() {
            const interval = setInterval(() => {
                if (vippsPopup && vippsPopup.closed) {
                    clearInterval(interval)

                    this.$emit('result', this.status)

                    let closeTimer = setTimeout(() => {
                        this.closeModal()
                        clearTimeout(closeTimer)
                    }, 1000)
                }
            }, 1000)
        },

        listenMessage(value) {
            if (value) {
                const self = this
                window.addEventListener('message', function (e) {
                    if (typeof e.data != 'string') {
                        return
                    }
                    if (e.data.indexOf('webpack') > -1) {
                        return
                    }

                    try {
                        let payload = JSON.parse(e.data)

                        if (payload.action == 'vipps-payment-start') {
                            self.lastAction = 'vipps-payment-start'
                            self.checkIsPopupClose()
                            return
                        }

                        if (payload.action == 'vipps-close-landing' && payload.status == 'CLOSE') {
                            self.lastAction = 'vipps-close-landing'
                            value.close()
                            return
                        }

                        if (payload.action == self.lastAction) {
                            return
                        }

                        if (payload.action == 'vipps-payment-info') {
                            self.lastAction = 'vipps-payment-info'
                            self.status = payload.status
                            return
                        }

                        return
                    } catch (e) {
                        value.close()
                        return
                    }
                })
            }
        }
    }
}
</script>

<style lang="scss">
.vipps-modal {
    font-family: 'Open Sans', sans-serif !important;
    background-color: rgba(0, 0, 0, 0.2);
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: start;
    font-size: 1.2em;
    z-index: 9999;

    .modal-content {
        background-color: #fff;
        margin-top: 4rem;
        padding: 1rem 1.5rem;
        width: 500px !important;
        overflow: hidden;
        border-radius: 3px;
        box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);

        @media screen and (max-width: 500px) {
            width: 90% !important;
            margin: 1rem 0.5rem;
        }

        .subscription-box {
            max-width: 50%;
        }

        .subscription-input {
            width: 30%;
            background-color: #eeeeee;
        }

        .modal-body {
            margin: 0 0 1rem 0;

            @media screen and (max-width: 500px) {
                margin: 0 0 1rem 0;

                .subscription-box {
                    max-width: 100%;
                }

                .subscription-input {
                    width: 30%;
                    background-color: #eeeeee;
                }
            }
        }
    }

    .payment-loading {
        margin: -1rem 0 0 -1.5rem;
    }
}

.vipps-logo {
    width: 15em;
    height: 100%;
}

.vipps-open-button {
    width: 100%;
    font-family: 'Open Sans', sans-serif;
    font-size: 0.8em;
    background-color: #ff6a21 !important;
    padding: 0.6rem 0;
    margin-top: 3rem;

    &:hover {
        background-color: #ff6b21cc !important;
    }

    span {
        font-weight: normal;
    }
}

.vipps-title {
    font-size: 1.5rem;
}
</style>
